'use client';

import React from 'react';
import { Box, Container } from '@mui/material';
import { Flame } from 'lucide-react';

import { EmptyState, ErrorState } from '@/components/';
import { useAuth, useSignInHandlers, useT } from '@/hooks';
import { useStreakStatus } from '@/services/query/streak.queries';
import { StreakBadgesShowcase } from './StreakBadgesShowcase';
import { StreakCycleTracker } from './StreakCycleTracker';
import { StreakFreezeCard } from './StreakFreezeCard';
import { StreakHero } from './StreakHero';
import { StreakHistorySection } from './StreakHistorySection';
import { StreakHowItWorks } from './StreakHowItWorks';
import { StreakLeaderboardSection } from './StreakLeaderboardSection';
import { StreaksSkeleton } from './StreaksSkeleton';
import { StreakStatsRow } from './StreakStatsRow';

/**
 * StreakDashboard
 * The main container for the /streaks page. Handles auth-gating and data
 * orchestration; every section below reads from the single `useStreakStatus`
 * call except the leaderboard and history, which have their own queries.
 */
export const StreakDashboard: React.FC = () => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { onLogin } = useSignInHandlers();
  const { t } = useT();
  const { data, isLoading: isDataLoading, error } = useStreakStatus(isAuthenticated);

  const isLoading = isAuthLoading || (isAuthenticated && isDataLoading);

  if (isLoading) {
    return <StreaksSkeleton />;
  }

  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ pt: 4, pb: 2 }}>
          <StreakEyebrow />
        </Box>
        <Box sx={{ borderRadius: 6, border: '1px solid', borderColor: 'divider', p: 6 }}>
          <EmptyState
            title={t('streaks.joinTitle', { defaultValue: 'Start your first streak' })}
            description={t('streaks.joinDesc', {
              defaultValue:
                'Log in and trade today to start earning points, badges, and a leaderboard spot.',
            })}
            actionLabel={t('streaks.loginToStart', { defaultValue: 'Log in to get started' })}
            onAction={onLogin}
            icon={<Flame color="#e8b046" size={32} />}
          />
        </Box>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorState
          title={t('streaks.unavailableTitle', { defaultValue: 'Streaks unavailable' })}
          message={t('streaks.unavailableMsg', {
            defaultValue: "We couldn't load your streak right now. Try again shortly.",
          })}
        />
      </Container>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <StreakHero data={data} />
        <StreakStatsRow data={data} />
        <StreakCycleTracker data={data} />
        <StreakHowItWorks />
        <StreakBadgesShowcase data={data} />
        <StreakFreezeCard data={data} />
        <StreakLeaderboardSection />
        <StreakHistorySection />
      </Box>
    </Box>
  );
};

const StreakEyebrow: React.FC = () => {
  const { t } = useT();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '0.7rem',
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: '#e8b046',
      }}
    >
      <Flame size={14} />
      <span>{t('streaks.eyebrow', { defaultValue: 'Trading Streak' })}</span>
    </Box>
  );
};
